import { create } from 'zustand';
import { User as FirebaseUser } from 'firebase/auth';
import type { User } from '@perfin/shared';
import {
  signInWithEmail,
  signUpWithEmail,
  signOut,
  sendPasswordReset,
  getUserProfile,
  updateUserProfile,
  subscribeToAuthState,
  ensureUserProfile,
  clearMfaVerifiedOnLogin,
  verifyMfaCodeServerSide,
  createSession,
  logFailedLogin,
} from '@perfin/shared';
import { performLogout } from '@/services';

interface AuthState {
  user: FirebaseUser | null;
  profile: User | null;
  loading: boolean;
  error: string | null;

  // MFA state
  mfaPending: boolean;
  mfaSetupRequired: boolean;
  pendingUser: FirebaseUser | null;

  // Actions
  initialize: () => () => void;
  login: (email: string, password: string) => Promise<{ mfaRequired: boolean; mfaSetupRequired: boolean }>;
  signup: (email: string, password: string, displayName?: string) => Promise<{ mfaSetupRequired: boolean }>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: (data: Partial<Pick<User, 'displayName' | 'defaultCurrency'>>) => Promise<void>;
  clearError: () => void;

  // MFA actions
  verifyMfa: (code: string) => Promise<{ success: boolean; usedBackupCode: boolean }>;
  cancelMfa: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  profile: null,
  loading: true,
  error: null,
  mfaPending: false,
  mfaSetupRequired: false,
  pendingUser: null,

  initialize: () => {
    // Track if this initialization is still active (for StrictMode race prevention)
    let isActive = true;

    // Failsafe timeout: if auth takes longer than 3 seconds to resolve, force load completion
    // This catches issues where IndexedDB or third-party cookies block Firebase from returning
    const failsafeTimeout = setTimeout(() => {
      if (isActive && get().loading) {
        console.warn('Auth initialization timed out after 3 seconds. Forcing load to false.');
        set({ loading: false });
      }
    }, 3000);

    // Subscribe for real-time auth state updates
    const unsubscribe = subscribeToAuthState(async (user) => {
      // Prevent state updates if this initialization was cancelled
      if (!isActive) {
        clearTimeout(failsafeTimeout);
        return;
      }

      // Don't override state if MFA setup/verification is pending
      const currentState = get();
      if (currentState.mfaPending || currentState.mfaSetupRequired) {
        clearTimeout(failsafeTimeout);
        return;
      }

      if (user) {
        try {
          // Ensure user profile exists (has built-in idempotency check)
          await ensureUserProfile(user);
          // Check again after async operation
          if (!isActive) return;
          // Re-check MFA state after async operation
          const stateAfterAsync = get();
          if (stateAfterAsync.mfaPending || stateAfterAsync.mfaSetupRequired) {
            return;
          }
          const profile = await getUserProfile(user.uid);
          if (!isActive) return;

          clearTimeout(failsafeTimeout);
          set({ user, profile, loading: false });
        } catch (error) {
          console.error('Auth initialization error:', error);
          if (isActive) {
            clearTimeout(failsafeTimeout);
            set({ user: null, profile: null, loading: false });
          }
        }
      } else {
        clearTimeout(failsafeTimeout);
        set({ user: null, profile: null, loading: false });
      }
    });

    // Return cleanup function that marks this initialization as inactive
    return () => {
      isActive = false;
      unsubscribe();
    };
  },

  login: async (email, password) => {
    // Set MFA pending BEFORE Firebase login to prevent auth listener race condition
    set({ loading: true, error: null, mfaPending: true, mfaSetupRequired: false, pendingUser: null });
    try {
      const user = await signInWithEmail(email, password);

      // Clear mfaVerified claim and check if MFA is required (server-side)
      const { mfaRequired, mfaSetupRequired } = await clearMfaVerifiedOnLogin();

      if (mfaRequired) {
        // Force token refresh to get updated claims
        await user.getIdToken(true);
        // MFA required - update state with setup status
        set({ loading: false, mfaPending: true, mfaSetupRequired, pendingUser: user });
        return { mfaRequired: true, mfaSetupRequired };
      }

      // No MFA required - complete login (mfaSkip enabled by superAdmin)
      await user.getIdToken(true); // Refresh token to get mfaVerified: true claim
      try {
        await createSession();
      } catch (sessionError) {
        console.warn('Failed to create session, continuing anyway:', sessionError);
      }
      const profile = await getUserProfile(user.uid);
      set({ user, profile, loading: false, mfaPending: false, mfaSetupRequired: false });
      return { mfaRequired: false, mfaSetupRequired: false };
    } catch (error: unknown) {
      // On error, clear MFA flags
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      set({ error: errorMessage, loading: false, mfaPending: false, mfaSetupRequired: false });

      // Log failed login attempt (fire-and-forget, don't await)
      logFailedLogin(email).catch(() => {});

      throw error;
    }
  },

  signup: async (email, password, displayName) => {
    // Set MFA flags BEFORE Firebase signup to prevent auth listener race condition
    set({ loading: true, error: null, mfaPending: true, mfaSetupRequired: true, pendingUser: null });
    try {
      const user = await signUpWithEmail(email, password, displayName);

      // Set the pending user (MFA flags already set)
      set({ loading: false, pendingUser: user });
      return { mfaSetupRequired: true };
    } catch (error: unknown) {
      // On error, clear MFA flags
      const errorMessage = error instanceof Error ? error.message : 'Signup failed';
      set({ error: errorMessage, loading: false, mfaPending: false, mfaSetupRequired: false });
      throw error;
    }
  },

  logout: async () => {
    set({ loading: true, error: null });
    try {
      // Use comprehensive logout service
      await performLogout({ reason: 'user' });
      // State is already reset by performLogout, but ensure loading is false
      set({ loading: false });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Logout failed';
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  resetPassword: async (email) => {
    set({ loading: true, error: null });
    try {
      await sendPasswordReset(email);
      set({ loading: false });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Password reset failed';
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  updateProfile: async (data) => {
    const { user } = get();
    if (!user) return;

    set({ loading: true, error: null });
    try {
      await updateUserProfile(user.uid, data);
      const profile = await getUserProfile(user.uid);
      set({ profile, loading: false });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Profile update failed';
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  clearError: () => set({ error: null }),

  // MFA verification (server-side)
  verifyMfa: async (code) => {
    const { pendingUser } = get();
    if (!pendingUser) {
      throw new Error('No pending MFA verification');
    }

    set({ loading: true, error: null });
    try {
      const result = await verifyMfaCodeServerSide(code);

      if (result.success) {
        // MFA verified - force token refresh to get new claims
        await pendingUser.getIdToken(true);

        // Create server-side session for timeout tracking
        try {
          await createSession();
        } catch (sessionError) {
          console.warn('Failed to create session, continuing anyway:', sessionError);
          // Don't block login if session creation fails
        }

        // Now complete login
        const profile = await getUserProfile(pendingUser.uid);
        set({
          user: pendingUser,
          profile,
          loading: false,
          mfaPending: false,
          pendingUser: null,
        });
      } else {
        set({ loading: false, error: 'Invalid verification code' });
      }

      return result;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'MFA verification failed';
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  // Cancel MFA and sign out
  cancelMfa: async () => {
    set({ loading: true });
    try {
      await signOut();
      set({
        user: null,
        profile: null,
        loading: false,
        mfaPending: false,
        mfaSetupRequired: false,
        pendingUser: null,
        error: null,
      });
    } catch {
      set({ loading: false, mfaPending: false, mfaSetupRequired: false, pendingUser: null });
    }
  },
}));
